import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import YRayViewer from '../components/yray/YRayViewer';
import { ExtractionStatus, Timeline, YFrame } from '../components/yray/types';
import { isOwnerAccess, ProjectAccess } from '../hooks/useProjectAccess';
import { uploadToR2 } from '../utils/r2Upload';
import './YRay.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const POLL_MS = 4000;

const LANGUAGES: { code: string; name: string }[] = [
  { code: 'auto', name: 'Auto-detect' },
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ar', name: 'Arabic' },
];

interface ProjectLite { id: string; name: string; platform?: string; audience?: string; access?: ProjectAccess }
interface VideoLite { id: string; name: string | null; file_name: string; duration?: number | null; status?: string }

const fmtDuration = (seconds: number | null | undefined): string => {
  if (!seconds || seconds <= 0) return '';
  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
};

const isActive = (s: ExtractionStatus | null): boolean => {
  const st = s?.latest?.status;
  return !!st && st !== 'completed' && st !== 'failed';
};

/**
 * Y-Ray: video metadata extraction viewer for one project. Starts a run (POST extract-metadata),
 * polls its status, and shows the result once the worker has stored the timeline.
 */
const YRay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [project, setProject] = useState<ProjectLite | null>(null);
  const [videos, setVideos] = useState<VideoLite[]>([]);
  const [loadingProject, setLoadingProject] = useState(true);
  const [status, setStatus] = useState<ExtractionStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [frames, setFrames] = useState<YFrame[]>([]);
  const [language, setLanguage] = useState('auto');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const owner = isOwnerAccess(project?.access);
  // No video is preselected: the user picks an uploaded one or uploads a new one.
  const videoId = searchParams.get('video') || '';
  const uploading = uploadPct !== null;

  // Project and its videos. Story renders leave a generated video row behind; those are not footage.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const [pRes, vRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${id}`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/projects/${id}/videos`, { credentials: 'include' }),
        ]);
        if (!pRes.ok) {
          throw new Error(
            pRes.status === 401 ? 'Your session has expired. Please sign in again.'
              : pRes.status === 404 ? 'Project not found for this account.'
              : `Could not load the project (HTTP ${pRes.status}).`
          );
        }
        const p = await pRes.json();
        const list: VideoLite[] = vRes.ok ? await vRes.json() : [];
        if (cancelled) return;
        setProject({ id: p.id, name: p.name, platform: p.platform, audience: p.audience, access: p.access });
        setVideos(list.filter(v => !v.file_name.startsWith('image_project_')));
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load project');
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const loadStatus = useCallback(async (vid: string, quiet = false) => {
    if (!quiet) setStatusLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/videos/${vid}/extraction`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 404 ? 'Video not found' : 'Failed to load Y-Ray status');
      setStatus(await res.json());
      setError('');
    } catch (e: any) {
      if (!quiet) setError(e.message || 'Failed to load Y-Ray status');
    } finally {
      if (!quiet) setStatusLoading(false);
    }
  }, []);

  // Switching video starts from a clean slate.
  useEffect(() => {
    setStatus(null);
    setTimeline(null);
    setFrames([]);
    setError('');
    setLimitReached(false);
    if (videoId && owner) void loadStatus(videoId);
  }, [videoId, owner, loadStatus]);

  // Poll while a run is in progress.
  const active = isActive(status);
  useEffect(() => {
    if (!active || !videoId) return;
    const t = setInterval(() => { void loadStatus(videoId, true); }, POLL_MS);
    return () => clearInterval(t);
  }, [active, videoId, loadStatus]);

  // Fetch the timeline once per finished run (a new jobId means a new result).
  const resultJobId = status?.result?.jobId;
  useEffect(() => {
    if (!resultJobId || !videoId) { setTimeline(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/videos/${videoId}/metadata`, { credentials: 'include' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Could not load the Y-Ray result');
        }
        const data = await res.json();
        if (!cancelled) setTimeline(data);
        // Thumbnails are optional; a failure just leaves the scene cards without them.
        const fr = await fetch(`${API_BASE}/api/videos/${videoId}/frames`, { credentials: 'include' }).catch(() => null);
        const fd = fr && fr.ok ? await fr.json().catch(() => null) : null;
        if (!cancelled) setFrames(Array.isArray(fd?.frames) ? fd.frames : []);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Could not load the Y-Ray result');
      }
    })();
    return () => { cancelled = true; };
  }, [resultJobId, videoId]);

  // Same two steps the shorts and caption uploads use (init → direct PUT), minus their confirm step:
  // nothing is queued or charged until the user presses Run Y-Ray.
  const handleUpload = async (file: File) => {
    if (!id || uploading) return;
    if (!file.type.startsWith('video/')) { setError('Choose a video file.'); return; }
    setError('');
    setLimitReached(false);
    setUploadPct(0);
    try {
      const baseName = file.name.replace(/\.[^.]+$/, '');
      const initRes = await fetch(`${API_BASE}/api/projects/${id}/videos/init`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          name: baseName,
          platform: project?.platform || 'youtube_shorts',
          audience: project?.audience || 'general',
        }),
      });
      if (!initRes.ok) {
        const data = await initRes.json().catch(() => ({}));
        throw new Error(data.error || 'Could not start the upload');
      }
      const { videoId: newId, presignedUrl } = await initRes.json();
      await uploadToR2(presignedUrl, file, p => setUploadPct(p.percent));
      setVideos(prev => [{ id: newId, name: baseName, file_name: file.name, duration: null, status: 'pending' }, ...prev]);
      setSearchParams({ video: newId }, { replace: true });
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploadPct(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const start = async (force: boolean) => {
    if (!videoId || starting) return;
    if (force && !window.confirm('Re-run Y-Ray on this video? It repeats the full analysis, takes several minutes, and uses 1 more credit.')) return;
    setStarting(true);
    setError('');
    setLimitReached(false);
    try {
      const res = await fetch(`${API_BASE}/api/videos/${videoId}/extract-metadata`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force, language }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // 409 with a jobId: a run is already going, so showing it is the same as having started it.
        if (res.status === 409 && data.jobId) {
          await loadStatus(videoId, true);
          return;
        }
        if (res.status === 402) {
          setLimitReached(true);
          return;
        }
        if (res.status === 403 || res.status === 409) {
          throw new Error(data.error || 'Y-Ray is not available on your plan.');
        }
        throw new Error(data.error || 'Failed to start Y-Ray');
      }
      await loadStatus(videoId, true);
    } catch (e: any) {
      setError(e.message || 'Failed to start Y-Ray');
    } finally {
      setStarting(false);
    }
  };

  const run = status?.latest || null;
  const failed = run?.status === 'failed';
  const videoLabel = (v: VideoLite) => v.name || v.file_name;
  const selected = videos.find(v => v.id === videoId);
  const selectedLabel = selected ? videoLabel(selected) : status?.video ? (status.video.name || status.video.fileName) : 'Selected video';
  const ready = !!timeline && !!status?.result;
  // A finished run can be re-run even when its result file failed to load (otherwise nothing offers it).
  const canRerun = !!status?.result && !active;

  const progressPct = useMemo(() => (run && run.progress > 0 ? Math.min(run.progress, 100) : null), [run]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="breadcrumb">
          <BreadcrumbBack to={`/projects/${id}`} />
          <Link to="/projects">Projects</Link>
          <span className="breadcrumb-sep">›</span>
          <Link to={`/projects/${id}`}>{project?.name || '…'}</Link>
          <span className="breadcrumb-sep">›</span>
          <span>Y-Ray</span>
        </div>

        <div className="yr-head">
          <div className="yr-head-title">
            <h2>Y-Ray</h2>
            <p className="yr-sub">See who is on screen, what they do and what is said, moment by moment.</p>
          </div>
          {owner && videoId && (
            <div className="yr-controls">
              <div className="yr-field yr-picked">
                <span>Video</span>
                <div className="yr-picked-row">
                  <b className="yr-vname" title={selectedLabel}>{selectedLabel}</b>
                  <button type="button" className="btn-secondary yr-btn" onClick={() => setSearchParams({}, { replace: true })}>
                    Change
                  </button>
                </div>
              </div>
              <label className="yr-field">
                <span>Language</span>
                <select value={language} onChange={e => setLanguage(e.target.value)} disabled={active}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                </select>
              </label>
              {canRerun && (
                <button type="button" className="btn-secondary yr-btn" onClick={() => start(true)} disabled={starting}>
                  Re-run
                </button>
              )}
            </div>
          )}
        </div>

        {error && <div className="error-banner">⚠️ {error}</div>}
        {limitReached && (
          <div className="error-banner">
            ⚠️ You have used all of your included runs. <Link to="/account/billing">Open billing</Link> to add more, then run Y-Ray again.
          </div>
        )}

        {loadingProject ? (
          <div className="yr-card yr-center"><div className="spinner" /></div>
        ) : !owner ? (
          <div className="yr-card">Only the project owner can run Y-Ray.</div>
        ) : !videoId ? (
          <div className="yr-pick">
            <section className="yr-card">
              <div className="yr-run-title">Use an uploaded video</div>
              {videos.length === 0 ? (
                <p className="yr-mut">This project has no videos yet. Upload one on the right.</p>
              ) : (
                <ul className="yr-vlist">
                  {videos.map(v => (
                    <li key={v.id}>
                      <button type="button" className="yr-vitem" onClick={() => setSearchParams({ video: v.id })}>
                        <span className="yr-vname">{videoLabel(v)}</span>
                        <span className="yr-mut yr-vmeta">{fmtDuration(v.duration)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="yr-card">
              <div className="yr-run-title">Upload a new video</div>
              <p className="yr-mut">It is added to this project. Nothing is charged until you run Y-Ray on it (1 credit).</p>
              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) void handleUpload(f); }}
              />
              <button type="button" className="btn-primary yr-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? `Uploading… ${uploadPct}%` : 'Choose video'}
              </button>
              {uploading && (
                <div className="yr-progress yr-upload-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadPct ?? 0}>
                  <div className="yr-progress-fill" style={{ width: `${uploadPct}%` }} />
                </div>
              )}
            </section>
          </div>
        ) : statusLoading && !status ? (
          <div className="yr-card yr-center"><div className="spinner" /></div>
        ) : (
          <>
            {active && (
              <div className="yr-card yr-run">
                <div className="yr-run-title">
                  {run?.status === 'queued' ? 'Waiting for a worker…' : 'Analyzing the video…'}
                </div>
                <div className="yr-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPct ?? undefined}>
                  <div className={`yr-progress-fill${progressPct == null ? ' yr-indeterminate' : ''}`} style={progressPct != null ? { width: `${progressPct}%` } : undefined} />
                </div>
                <p className="yr-mut">This can take several minutes for a long video. You can leave this page; the run continues.</p>
              </div>
            )}

            {failed && !active && (
              <div className="yr-card yr-failed">
                <div className="yr-run-title">The last Y-Ray run failed</div>
                <p className="yr-mut">{run?.error || 'Unknown error.'}</p>
                <button type="button" className="btn-primary yr-btn" onClick={() => start(false)} disabled={starting}>
                  {starting ? 'Starting…' : 'Try again'}
                </button>
              </div>
            )}

            {!run && !active && (
              <div className="yr-card yr-empty">
                <div className="yr-run-title">Run Y-Ray on this video</div>
                <p className="yr-mut">It analyses scenes, people, actions and speech once, then you can browse them alongside the video. A run uses 1 credit from your shorts allowance.</p>
                <button type="button" className="btn-primary yr-btn" onClick={() => start(false)} disabled={starting}>
                  {starting ? 'Starting…' : 'Run Y-Ray'}
                </button>
              </div>
            )}

            {ready && timeline && status?.result && (
              <YRayViewer timeline={timeline} videoUrl={status.result.videoUrl} frames={frames} />
            )}
            {status?.result && !timeline && !error && (
              <div className="yr-card yr-center"><div className="spinner" /></div>
            )}
            {status?.result && !timeline && !!error && !active && (
              <div className="yr-card yr-failed">
                <div className="yr-run-title">The result could not be loaded</div>
                <p className="yr-mut">Run Y-Ray again to recreate it. This repeats the analysis and uses 1 credit.</p>
                <button type="button" className="btn-primary yr-btn" onClick={() => start(true)} disabled={starting}>
                  {starting ? 'Starting…' : 'Run again'}
                </button>
              </div>
            )}
            {run?.status === 'completed' && !run.hasResult && (
              <div className="yr-card yr-failed">
                <div className="yr-run-title">The run finished, but no result was stored</div>
                <p className="yr-mut">The extraction worker did not save the timeline. Run again, and if this repeats, ask an admin to check the worker.</p>
                <button type="button" className="btn-primary yr-btn" onClick={() => start(true)} disabled={starting}>
                  {starting ? 'Starting…' : 'Run again'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default YRay;
